// 工单面板: 导航栏右侧的「工单」按钮(显示红点提示)
// 点击后弹出侧抽屉, 包含: 我的工单列表 + 提交工单 + 查看/回复详情
import React, { useEffect, useState } from 'react';
import { Button, Modal, Input, TextArea, Toast, Spin, Empty, Tag, SideSheet, Badge } from '@douyinfe/semi-ui';
import { IconCustomerSupport } from '@douyinfe/semi-icons';
import { API_USER } from '../helpers';
import { pageVisible } from '../helpers/utils';
import { useUserShared } from './UserPanel';
import { useT } from '../i18n';

// —— 状态映射 ——
// 颜色 hardcoded 以适配主题; 文案走 i18n,留作 fallback
const STATUS_FALLBACK = { 1: { text: '进行中', color: 'orange' }, 2: { text: '已关闭', color: 'grey' } };
const STATUS_I18N = { 1: { i18nKey: '工单.状态.进行中', color: 'orange' }, 2: { i18nKey: '工单.状态.已关闭', color: 'grey' } };

// —— 已读时间戳存储键 ——
const READ_KEY = 'mt_tickets_last_read';

// —— 主组件: 渲染导航栏按钮 ——
const TicketsButton = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [readAt, setReadAt] = useState(() => {
    try { return parseInt(sessionStorage.getItem(READ_KEY) || '0', 10) || 0; } catch { return 0; }
  });
  const userShared = useUserShared();
  const apikey = userShared.apikey;
  const queried = userShared.queried;
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (!queried || !apikey) { setTickets([]); return; }
    let alive = true;
    API_USER.get('/api/tickets', { headers: { Authorization: `Bearer ${apikey}` } })
      .then((r) => { if (alive && r.data?.success) setTickets(r.data.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apikey, queried, reloadKey]);

  useEffect(() => {
    if (!queried || !apikey) return undefined;
    const id = setInterval(() => {
      if (!pageVisible()) return; // 页面看不见时别白打接口
      setReloadKey((k) => k + 1);
    }, 30000);
    return () => clearInterval(id);
  }, [apikey, queried]);

  const hasUnread = tickets.some((tk) =>
    tk.status === 1 &&
    tk.last_reply_by === 2 &&
    Number(tk.updated_at || 0) > readAt
  );

  const disabled = !queried || !apikey;

  const markRead = () => {
    const now = Date.now();
    try { sessionStorage.setItem(READ_KEY, String(now)); } catch (_) {}
    setReadAt(now);
  };

  const btn = (
    <Button
      theme="borderless"
      type="tertiary"
      icon={<IconCustomerSupport style={{ fontSize: 18 }} />}
      aria-label={t('工单.按钮', '工单')}
      onClick={() => {
        if (disabled) return;
        setOpen(true);
        markRead();
      }}
      disabled={disabled}
      style={{ padding: '4px 8px' }}
    />
  );

  return (
    <>
      {hasUnread ? <Badge count={1} type="danger" dot>{btn}</Badge> : btn}

      <SideSheet
        title={t('工单.抽屉标题', '我的工单')}
        visible={open}
        onCancel={() => setOpen(false)}
        width={460}
        placement="right"
        headerStyle={{ borderBottom: '1px solid #e5e6eb' }}
      >
        <TicketsList
          apikey={apikey}
          onChanged={() => setReloadKey((k) => k + 1)}
          onView={markRead}
        />
      </SideSheet>
    </>
  );
};

// —— 抽屉内的列表 + 提交按钮 + 详情 ——
const TicketsList = ({ apikey, onChanged, onView }) => {
  const t = useT();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // 列表内容指纹:只有真的变了才通知父级刷新铃铛。
  // 以前每次 load 完都调 onChanged → 父级 reloadKey+1 → 父级再打一次 /api/tickets,
  // 一次列表加载引发两次请求(父级自己还有 30 秒轮询)。
  const sigRef = React.useRef('');

  const load = () => {
    setLoading(true);
    API_USER.get('/api/tickets', { headers: { Authorization: `Bearer ${apikey}` } })
      .then((r) => {
        if (!r.data?.success) return;
        const next = r.data.data || [];
        setTickets(next);
        const sig = next.map((x) => x.id + ':' + (x.updated_at || 0)).join(',');
        if (sig !== sigRef.current) {
          sigRef.current = sig;
          onChanged && onChanged();
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadKey]);

  const statusLabel = (s) => {
    const m = STATUS_I18N[s] || STATUS_FALLBACK[s];
    if (!m) return t('工单.未知状态', '未知');
    if (m.i18nKey) return t(m.i18nKey, m.text);
    return m.text;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Button type="primary" theme="solid" size="small" onClick={() => setCreating(true)}>
          {t('工单.提交按钮', '提交工单')}
        </Button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin spinning /></div>
        ) : tickets.length === 0 ? (
          <Empty description={t('工单.空提示', '暂无工单,遇到问题可点上方提交')} style={{ padding: 30 }} />
        ) : (
          tickets.map((tk) => (
            <div
              key={tk.id}
              onClick={() => setActiveId(tk.id)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e5e6eb',
                borderRadius: 6,
                marginBottom: 8,
                cursor: 'pointer',
                background: tk.last_reply_by === 2 && tk.status === 1 ? '#fffbe6' : '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2329', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  #{tk.id} · {tk.subject}
                </span>
                <Tag color={STATUS_FALLBACK[tk.status]?.color || 'grey'} size="small">
                  {statusLabel(tk.status)}
                </Tag>
              </div>
              <div style={{ fontSize: 12, color: '#86909c', marginTop: 4 }}>
                {t('工单.消息条数', '{n} 条消息', { n: tk.msg_count })}
                · {new Date(tk.updated_at).toLocaleString('zh-CN', { hour12: false })}
                {tk.last_reply_by === 2 && tk.status === 1 && (
                  <span style={{ marginLeft: 8, color: '#f56565', fontWeight: 500 }}>
                    {t('工单.未读', '● 管理员已回复')}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <CreateTicketModal
        visible={creating}
        apikey={apikey}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); setReloadKey((k) => k + 1); }}
      />
      <TicketDetailModal
        ticketId={activeId}
        apikey={apikey}
        onView={onView}
        onClose={() => { setActiveId(null); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
};

// ====== 提交工单 ======
const CreateTicketModal = ({ visible, apikey, onClose, onCreated }) => {
  const t = useT();
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (!visible) { setSubject(''); setContent(''); } }, [visible]);

  const submit = async () => {
    if (!subject.trim()) return Toast.error(t('工单.主题必填', '请输入主题'));
    if (!content.trim()) return Toast.error(t('工单.内容必填', '请输入问题描述'));
    setSubmitting(true);
    try {
      const r = await API_USER.post(
        '/api/tickets',
        { subject: subject.trim(), content: content.trim() },
        { headers: { Authorization: `Bearer ${apikey}` } }
      );
      if (r.data?.success) {
        Toast.success(t('工单.提交成功', '工单已提交,管理员会尽快回复'));
        onCreated();
      } else {
        Toast.error(r.data?.message || t('工单.提交失败', '提交失败'));
      }
    } catch (e) {
      Toast.error(e.response?.data?.message || t('工单.提交失败', '提交失败'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('工单.提交弹窗标题', '提交工单')}
      visible={visible}
      onCancel={onClose}
      onOk={submit}
      okText={t('公共.确认', '提交')}
      cancelText={t('公共.取消', '取消')}
      confirmLoading={submitting}
      width={480}
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>
            {t('工单.主题', '主题')}
          </label>
          <Input
            value={subject}
            onChange={setSubject}
            placeholder={t('工单.主题占位符', '一句话描述您的问题')}
            maxLength={100}
            showClear
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>
            {t('工单.内容', '问题详情')}
          </label>
          <TextArea
            value={content}
            onChange={setContent}
            placeholder={t('工单.内容占位符', '请详细描述您遇到的问题')}
            rows={5}
            maxCount={2000}
          />
        </div>
      </div>
    </Modal>
  );
};

// ====== 详情 + 回复 ======
const TicketDetailModal = ({ ticketId, apikey, onView, onClose }) => {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!ticketId) { setData(null); return; }
    setLoading(true);
    API_USER.get(`/api/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${apikey}` } })
      .then((r) => {
        if (r.data?.success) {
          setData(r.data.data);
          if (onView) onView();
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticketId, reloadKey]);

  const sendReply = async () => {
    if (!reply.trim()) return Toast.error(t('工单.回复必填', '请输入回复'));
    setReplying(true);
    try {
      const r = await API_USER.post(
        `/api/tickets/${ticketId}/reply`,
        { content: reply.trim() },
        { headers: { Authorization: `Bearer ${apikey}` } }
      );
      if (r.data?.success) { setReply(''); setReloadKey((k) => k + 1); Toast.success(t('工单.已回复', '已回复')); }
      else Toast.error(r.data?.message || t('工单.回复失败', '回复失败'));
    } catch (e) { Toast.error(e.response?.data?.message || t('工单.回复失败', '回复失败')); }
    finally { setReplying(false); }
  };

  const ticket = data?.ticket;
  const messages = data?.messages || [];
  const closed = ticket?.status === 2;

  const statusLabel = (s) => {
    const m = STATUS_I18N[s] || STATUS_FALLBACK[s];
    if (!m) return t('工单.未知状态', '未知');
    if (m.i18nKey) return t(m.i18nKey, m.text);
    return m.text;
  };

  return (
    <Modal
      title={ticket ? `#${ticket.id} · ${ticket.subject}` : t('工单.详情标题', '工单详情')}
      visible={!!ticketId}
      onCancel={onClose}
      onOk={sendReply}
      okText={t('工单.发送', '发送回复')}
      cancelText={t('公共.取消', '关闭')}
      okButtonProps={{ type: 'primary', loading: replying, disabled: closed || !reply.trim() }}
      width={560}
      style={{ top: 40 }}
    >
      {loading || !data ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin spinning /></div>
      ) : (
        <div>
          <div style={{ fontSize: 12, color: '#86909c', marginBottom: 12 }}>
            <Tag color={STATUS_FALLBACK[ticket.status]?.color}>{statusLabel(ticket.status)}</Tag>
            · {t('工单.创建于', '创建于')} {new Date(ticket.created_at).toLocaleString('zh-CN', { hour12: false })}
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto', background: '#fafbfc', padding: 12, borderRadius: 6, marginBottom: 12 }}>
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  marginBottom: 10,
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: m.author_type === 2 ? '#e6f4ff' : '#fff',
                  border: m.author_type === 2 ? '1px solid #91caff' : '1px solid #e5e6eb',
                }}
              >
                <div style={{ fontSize: 12, color: '#86909c', marginBottom: 4 }}>
                  {m.author_type === 2 ? t('工单.作者管理员', '🛠 管理员') : t('工单.作者用户', '👤 我')}
                  · {new Date(m.created_at).toLocaleString('zh-CN', { hour12: false })}
                </div>
                <div style={{ fontSize: 14, color: '#1f2329', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>

          {closed ? (
            <div style={{ fontSize: 13, color: '#86909c', textAlign: 'center', padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
              {t('工单.已关闭', '此工单已关闭,无法继续回复')}
            </div>
          ) : (
            <TextArea
              value={reply}
              onChange={setReply}
              placeholder={t('工单.回复占位符', '输入回复…')}
              rows={3}
              maxCount={2000}
              disabled={replying}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default TicketsButton;